import { ConfigFeature } from '../../../../features/config/contract'
import { Config, DeviceDataSnapshot } from '../../../../types/config'
import { getAddress } from '../../../../utils/common'
import {
  ensureCybercafeConfigSlotNames,
  getCybercafeConfigSlots,
  getRoleMatchedSlots,
  toFirmwareIndex,
  fromFirmwareIndex,
} from '../../../../utils/configSlots'
import { ADDRESS_VT_nrf54L } from '../../protocols/address'
import { sendRead, sendWrite } from '../../capabilities/memory'
import { convertASCIIToStr, convertStrToASCII } from '../../../../utils/StringUtils'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import { CMD_VT_nrf54L, PROTOCOL_CONFIG_VT_nrf54L } from '../../protocols/cmd'
import { checkACK } from '../../capabilities/memory'
import { delay } from '../../../../utils/TimeUtils'
import { GearType } from '../../../../types/dpi'
import { LayerKeysMap, Macro, PressureMacro } from '../../../../types/mouseKeys'
import { MousePerfCfg, SensorModes } from '../../../../types/mousePerfCfg'
import { DeviceConfigPayload } from '../../types/nordic-nrf54l-config'
import { formatFirmwareVersions } from '../../../../utils/firmwareVersion'

const REPORT_ID = PROTOCOL_CONFIG_VT_nrf54L.report_id
const FEATURE_REPORT_ID = PROTOCOL_CONFIG_VT_nrf54L.feature_report_id
export class NordicNrf54LConfig extends ConfigFeature {
  async getCurrentConfig(): Promise<Config> {
    const rawIndex = await this.getCurrentConfigIndex()
    // 固件返回相对位置，转换为对外一致的绝对 slot id
    const matchedSlots = getRoleMatchedSlots(
      this.device.manifest.capabilities,
      this.device.configSlotRoles,
    )
    const index = fromFirmwareIndex(rawIndex, matchedSlots)
    const name = await this.getCurrentConfigName(index)
    this.device.currentConfig = {
      index,
      name,
    }
    return this.device.currentConfig
  }

  async getCurrentConfigIndex(): Promise<number> {
    const ADDRESS = ADDRESS_VT_nrf54L.CONFIG_CURRENT

    return sendRead(this.device, ADDRESS, 1, (data) => {
      return data.getUint8(4)
    })
  }

  private checkConfigIndex(index?: number) {
    if (index !== undefined && index > ADDRESS_VT_nrf54L.CONFIG_NAME.length - 1) {
      throw new DeviceError(
        DeviceErrorCode.MAX_LIMIT_REACHED,
        `Get config index ${index} is out of range`,
      )
    }
  }
  async getCurrentConfigName(index?: number): Promise<string> {
    this.checkConfigIndex(index)

    const realIndex = index !== undefined ? index : await this.getCurrentConfigIndex()
    const ADDRESS = getAddress(ADDRESS_VT_nrf54L.CONFIG_NAME, realIndex)

    return sendRead(this.device, ADDRESS, 0x10, (data) => {
      const first = data.getUint8(4)
      if (first === 0 || first === 255) {
        return ''
      }
      const array: string[] = []
      for (let i = 4; i < 20; i++) {
        const byte = data.getUint8(i)
        if (byte === 0) break
        array.push(byte.toString(16).padStart(2, '0'))
      }
      return convertASCIIToStr(array)
    })
  }

  private async getConfigs(filter: boolean = true): Promise<Config[]> {
    const ADDRESS = ADDRESS_VT_nrf54L.CONFIG_NAME
    const configs: Config[] = []
    for (const [index, _] of ADDRESS.entries()) {
      const name = await this.getCurrentConfigName(index)
      if (filter && name === '') {
        continue
      }
      configs.push({
        name: name,
        index: index,
      })
    }
    return configs
  }

  async getAllConfigs(): Promise<Config[]> {
    const cafeSlots = getCybercafeConfigSlots(this.device.manifest)
    if (cafeSlots) {
      await ensureCybercafeConfigSlotNames(
        cafeSlots,
        () => this.getConfigs(false),
        (name, index) => this.createConfig(name, index),
      )
    }

    // 如果是设备有配置槽位，则不过滤全取，交由配置槽位自行过滤
    const matchedSlots = getRoleMatchedSlots(
      this.device.manifest.capabilities,
      this.device.configSlotRoles,
    )
    const res = await this.getConfigs(!matchedSlots)
    this.device.configList = res
    return this.device.configList
  }

  async setCurrentConfig(index: number): Promise<void> {
    const matchedSlots = getRoleMatchedSlots(
      this.device.manifest.capabilities,
      this.device.configSlotRoles,
    )
    const firmwareIndex = toFirmwareIndex(index, matchedSlots)
    this.checkConfigIndex(firmwareIndex)
    const ADDRESS = ADDRESS_VT_nrf54L.CONFIG_CURRENT
    const res = await sendRead(this.device, ADDRESS, 2, (data) => {
      return [data.getUint8(4)]
    })
    res[0] = firmwareIndex
    res[1] = firmwareIndex

    const targetConfig = this.device.configList.find((config) => config.index === index)
    if (targetConfig) {
      this.device.currentConfig = targetConfig
    }
    return sendWrite(this.device, ADDRESS, res)
  }

  private convertAndCheckConfigName(name: string): number[] {
    name = name.trim()
    if (name.length === 0) {
      throw new DeviceError(DeviceErrorCode.WRITE_FAILED, '配置名称不能为空')
    }
    const data = convertStrToASCII(name)
    if (data.length > 0x10) {
      throw new DeviceError(DeviceErrorCode.MAX_LIMIT_REACHED, '配置名称过长')
    }
    while (data.length < 0x10) {
      data.push(0)
    }
    return data
  }
  async renameConfig(config: Config): Promise<void> {
    const ADDRESS = getAddress(ADDRESS_VT_nrf54L.CONFIG_NAME, config.index)
    // 转换名字为 ASCI 码
    const nameData = this.convertAndCheckConfigName(config.name)
    // ⚠️ tag-mapping: nrf54l 侧无任何读取端注册 `cfg:` tag(getCurrentConfigName 读写同地址
    //    CONFIG_NAME,已由 address-range 自动失效),故下方 cfg 失效是 no-op,连同内容一并注释保留作提醒;
    //    若将来新增「全局地址 / 跨配置共享」的鼠标读取,需先在读取端补注册 cfg tag,再恢复此失效。
    return sendWrite(this.device, ADDRESS, nameData)
    // .then(() => {
    //   this.device.cache.invalidateByTag(`cfg:${config.index}`)
    // })
  }

  async createConfig(name: string, index?: number): Promise<number> {
    name = name.trim()
    if (name.length === 0) {
      throw new DeviceError(DeviceErrorCode.WRITE_FAILED, '配置名称不能为空')
    }
    const data = this.convertAndCheckConfigName(name)
    let targetIndex = index
    if (targetIndex === undefined) {
      const allConfigs = await this.getConfigs(false)
      targetIndex = -1
      for (const config of allConfigs) {
        if (config.name === '') {
          targetIndex = config.index
          break
        }
      }
      if (targetIndex === -1) {
        throw new DeviceError(DeviceErrorCode.MAX_LIMIT_REACHED, '无空闲配置')
      }
    }

    this.checkConfigIndex(targetIndex)

    const ADDRESS = getAddress(ADDRESS_VT_nrf54L.CONFIG_NAME, targetIndex)
    await sendWrite(this.device, ADDRESS, data)

    // 需要延迟500ms
    await delay(500)

    return targetIndex
  }

  async deleteConfig(index: number): Promise<void> {
    // 如果是当前使用的配置，需要先切换到默认配置再删除
    const currentConfig = await this.getCurrentConfig()
    if (currentConfig.index === index) {
      await this.setCurrentConfig(0)
    }
    // 鼠标的写入数据直接写入0x10长度的0
    const ADDRESS = getAddress(ADDRESS_VT_nrf54L.CONFIG_NAME, index)
    const emptyData = new Array(0x10).fill(0)
    await sendWrite(this.device, ADDRESS, emptyData)

    await delay(500)

    await sendWrite(this.device, ADDRESS, [])
    // ⚠️ tag-mapping: nrf54l 侧无读取端注册 `cfg:` tag,下方失效是 no-op,连同内容一并注释保留作提醒;
    //    删除是对 CONFIG_NAME[index] 的空数据写,与 getCurrentConfigName 同地址,已由 address-range 失效。
    //    若将来新增「全局地址 / 跨配置共享」的鼠标读取,需先在读取端补注册 cfg tag,再恢复此失效。
    // this.device.cache.invalidateByTag(`cfg:${index}`)
  }

  async initAllConfigs(): Promise<void> {
    const cmd = CMD_VT_nrf54L.init_default_flash

    const connectType = this.device.connectionType
    const payload = new Uint8Array([connectType, cmd[0], 0x52, 0x3d])

    const res = await this.device.sendAndWait(REPORT_ID, payload, FEATURE_REPORT_ID, (res) => {
      const ack = res.getUint8(0)
      return ack !== PROTOCOL_CONFIG_VT_nrf54L.busy_ack_value
    })

    checkACK(res)

    // 初始话需要主动清理缓存
    this.device.cache.clearAll()
    // 不在此处补名：恢复出厂后鼠标可能短暂不可达（尤其底座子设备），
    // 空槽默认名由 utils/configSlots + 各芯片 getAllConfigs 在设备就绪时懒补。
  }

  async getCurrentConfigSnapshot(): Promise<DeviceDataSnapshot> {
    const startTime = Date.now()
    const metadata = {
      deviceType: this.device.manifest.type,
      deviceModel: this.device.manifest.model,
      timestamp: startTime,
      sdkVersion: this.device.sdkVersion,
    }
    const payload = {
      configName: '',
      firmwareVersion: '',
      dpi: { gears: [] as GearType[], currentGearIndex: 0 },
      mouseKeys: { keyMap: [] as LayerKeysMap },
      macro: { macroList: [] as Macro[], pressureMacroList: [] as PressureMacro[] },
      performanceConfig: { cfg: {} as MousePerfCfg, sensorModes: [] as SensorModes },
    }

    // 环境校验
    const dpi = this.device.features.dpi
    const mouseKeys = this.device.features.mouseKeys
    const mousePerfCfg = this.device.features.mousePerfCfg
    const firmware = this.device.features.firmware
    if (!dpi || !mouseKeys || !mousePerfCfg || !firmware) {
      throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED_VERSION, '当前使用SDK功能不完整')
    }

    const updateProgress = (progress: number) => {
      this.device.emit('config:Upload_current_config', progress)
    }

    // ========== 1. 基础配置 (5%) ==========
    updateProgress(0)
    payload.configName = await this.getCurrentConfigName()
    updateProgress(2) // 细化为两个任务各分配一点
    payload.firmwareVersion = formatFirmwareVersions(await firmware.getFirmwareVersion())
    updateProgress(5)

    // ========== 2. DPI (10%，累计 15%) ==========
    payload.dpi.gears = await dpi.getGears(this.device.currentConfig.index)
    updateProgress(10)
    payload.dpi.currentGearIndex = await dpi.getCurrentGear(this.device.currentConfig.index)
    updateProgress(15)

    // ========== 3. 鼠标按键 (10%，累计 25%) ==========
    payload.mouseKeys.keyMap = await mouseKeys.getMouseKeyMap()
    updateProgress(25)

    // ========== 4. 宏配置 (累计 70%) ==========
    // 4.1 宏名称流 (5%，累计 30%)
    for await (const macroChunk of mouseKeys.getMacroListStream()) {
      payload.macro.macroList.push(...macroChunk)
    }
    updateProgress(30)

    // 4.2 动态宏详情进度 (40%，累计 70%)
    const macroList = payload.macro.macroList.filter((m) => m.index !== undefined)
    const pressureMacroLen = ADDRESS_VT_nrf54L.MACRO_PRESSURE_ADDRESS.length
    const totalMacroTasks = macroList.length + pressureMacroLen
    const macroDetailWeight = 40
    const macroBaseProgress = 30
    let completedMacroTasks = 0

    const updateMacroProgress = () => {
      const currentProgress =
        totalMacroTasks > 0
          ? macroBaseProgress +
            Math.round((macroDetailWeight * completedMacroTasks) / totalMacroTasks)
          : 70
      updateProgress(currentProgress)
    }

    // 标准宏详情
    for (let i = 0; i < macroList.length; i++) {
      const macro = macroList[i]
      console.log(`正在获取宏${macro.index}的详情`)
      const data = await mouseKeys.getMacroByIndex(macro.index!)
      Object.assign(macro, data)
      completedMacroTasks++
      updateMacroProgress()
    }

    // 压枪宏详情
    for (let i = 0; i < pressureMacroLen; i++) {
      console.log(`正在获取压枪宏${i}的详情`)
      const macro = await mouseKeys.getPressureMacroByIndex(i)
      payload.macro.pressureMacroList.push(macro)
      completedMacroTasks++
      updateMacroProgress()
    }
    updateProgress(70)

    // ========== 5. 性能配置 (30%，累计 100%) ==========
    // 将所有剩余进度分配给两个真实任务，使得最后一个任务完成时恰好到达 100%
    payload.performanceConfig.cfg = await mousePerfCfg.getAllPerfCfg()
    updateProgress(85) // 70 → 85 (15%)
    payload.performanceConfig.sensorModes = await mousePerfCfg.getSensorModeList()
    updateProgress(100) // 85 → 100 (15%)，所有数据获取完毕，进度立即满格

    console.log(`快照读取完成，总耗时：${Date.now() - startTime}ms`)
    return { metadata, payload }
  }

  async setCurrentConfigSnapshot(snapshot: DeviceDataSnapshot): Promise<void> {
    const startTime = Date.now()
    const { metadata, payload } = snapshot as DeviceDataSnapshot<DeviceConfigPayload>

    // 环境校验
    const dpi = this.device.features.dpi
    const mouseKeys = this.device.features.mouseKeys
    const mousePerfCfg = this.device.features.mousePerfCfg
    if (!dpi || !mouseKeys || !mousePerfCfg) {
      throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED_VERSION, '当前使用SDK功能不完整')
    }
    // 头文件校验
    if (metadata === undefined || metadata.sdkVersion === undefined) {
      throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED_VERSION, '当前使用SDK版本不匹配')
    }
    if (metadata.deviceModel !== this.device.manifest.model) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '设备匹配失败')
    }
    if (metadata.deviceType !== this.device.manifest.type) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '设备匹配失败2')
    }
    // 数据校验
    if (payload === undefined) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '数据异常')
    }

    const updateProgress = (progress: number) => {
      this.device.emit('config:Download_current_config', Math.round(progress))
    }

    // ---------- 定义所有可能步骤的基础权重 ----------
    const STEP_WEIGHTS = {
      dpi_gears: 10,
      dpi_gear: 10,
      mouseKeys: 10,
      macro_per_macro: 0, // 动态计算
      pressure_macro_per: 0, // 动态计算
      perf_cfg: 15,
      sensor_modes: 15,
    } as const

    // 动态计算宏相关权重
    let macroWeight = 0
    let pressureMacroWeight = 0
    const hasDpi = !!payload.dpi
    const hasMouseKeys = !!payload.mouseKeys
    const hasPerf = !!payload.performanceConfig

    // 统计宏数量
    const macroList = payload.macro?.macroList?.filter((m) => m.index !== undefined) ?? []
    const pressureMacroLen = payload.macro ? ADDRESS_VT_nrf54L.MACRO_PRESSURE_ADDRESS.length : 0
    const hasAnyMacro = payload.macro && (macroList.length > 0 || pressureMacroLen > 0)

    // 如果有宏，分配 40% 的总权重给整个宏部分，其中普通宏占 30%，压枪宏占 10%
    if (hasAnyMacro) {
      macroWeight = 30
      pressureMacroWeight = 10
    }

    // 计算每个普通宏和每个压枪宏的权重增量
    const perMacroIncrement = macroList.length > 0 ? macroWeight / macroList.length : 0
    const perPressureIncrement = pressureMacroLen > 0 ? pressureMacroWeight / pressureMacroLen : 0

    // ---------- 构建任务队列 ----------
    type Task = () => Promise<void>
    interface WeightedTask {
      task: Task
      weight: number
    }
    const queue: WeightedTask[] = []

    // 1. DPI 相关步骤
    if (hasDpi) {
      queue.push({
        weight: STEP_WEIGHTS.dpi_gears,
        task: async () => {
          await dpi.setDeviceGears(payload.dpi!.gears, this.device.currentConfig.index)
        },
      })
      queue.push({
        weight: STEP_WEIGHTS.dpi_gear,
        task: async () => {
          await dpi.setDeviceGear(payload.dpi!.currentGearIndex, this.device.currentConfig.index)
        },
      })
    }

    // 2. 鼠标按键
    if (hasMouseKeys) {
      queue.push({
        weight: STEP_WEIGHTS.mouseKeys,
        task: async () => {
          await mouseKeys.setMouseKeyMap(payload.mouseKeys!.keyMap)
        },
      })
    }

    // 3. 普通宏
    for (const macro of macroList) {
      queue.push({
        weight: perMacroIncrement,
        task: async () => {
          await mouseKeys.setMacroByIndex(macro, macro.index ?? 0)
        },
      })
    }

    // 4. 压枪宏
    for (let i = 0; i < pressureMacroLen; i++) {
      const macro = payload.macro!.pressureMacroList[i]
      if (!macro) {
        // 即使跳过，也保留权重占位，让进度继续前进
        queue.push({
          weight: perPressureIncrement,
          task: async () => {
            /* 空操作 */
          },
        })
        continue
      }
      queue.push({
        weight: perPressureIncrement,
        task: async () => {
          await mouseKeys.setPressureMacroByIndex(macro, i)
        },
      })
    }

    // 5. 性能配置
    if (hasPerf) {
      queue.push({
        weight: STEP_WEIGHTS.perf_cfg,
        task: async () => {
          await mousePerfCfg.setAllPerfCfg(payload.performanceConfig!.cfg)
        },
      })
      queue.push({
        weight: STEP_WEIGHTS.sensor_modes,
        task: async () => {
          await mousePerfCfg.setSensorModeList(payload.performanceConfig!.sensorModes)
        },
      })
    }

    // ---------- 执行任务并更新进度 ----------
    let currentProgress = 0
    updateProgress(currentProgress)

    for (const item of queue) {
      await item.task()
      currentProgress += item.weight
      updateProgress(currentProgress)
    }

    // 确保最终进度为 100（处理浮点数误差）
    updateProgress(100)

    console.log(`快照应用完成，总耗时：${Date.now() - startTime}ms`)
    // ⚠️ tag-mapping: nrf54l 侧无读取端注册 `cfg:` tag,下方失效是 no-op,连同内容一并注释保留作提醒;
    //    快照各项写入(DPI / 按键 / 宏 / 性能)均为同地址 sendWrite,已由 address-range 自动失效。
    //    若将来新增「全局地址 / 跨配置共享」的鼠标读取,需先在读取端补注册 cfg tag,再恢复此失效。
    // this.device.cache.invalidateByTag(`cfg:${this.device.currentConfig.index}`)
  }
}

// 兼容旧导出名，避免外部已经引用 VTNrf54LConfig 的代码在迁移期断开。
export { NordicNrf54LConfig as VTNrf54LConfig }
